import { useEffect, useRef, useState } from "react";
import { Nfc } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { api, errMsg } from "../../lib/api";
import { IconPicker } from "./IconPicker";
import { VoiceControls } from "./VoiceControls";
import { MemoryFields } from "./MemoryFields";
import { RiddleFields } from "./RiddleFields";
import { SpecimenIcon } from "../../lib/icons";

const EMPTY = { tag_uid: "", name: "", spore_word: "", sound_word: "", meaning: "", memory: { title: "", age: 9, year: 2007, script: "", clue: "", category: "Childhood" }, riddle: { fragment: "", question: "", answer: 5, hint: "", recall: "", recall_answer: 5 }, sfx_prompt: "", icon: "Sprout", voice: { texture: "hum", pitch: "mid" }, slot: "" };

export const SpecimenForm = ({ open, specimen, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const tagRef = useRef(null);
  const nameRef = useRef(null);

  useEffect(() => {
    if (open) setForm(specimen ? { ...EMPTY, ...specimen, slot: specimen.slot } : EMPTY);
  }, [open, specimen]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onTagKey = (e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      nameRef.current?.focus();
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...form, slot: form.slot ? Number(form.slot) : null };
    delete body.id;
    delete body.created_at;
    try {
      if (specimen) await api.put(`/admin/specimens/${specimen.id}`, body);
      else await api.post("/admin/specimens", body);
      toast.success(`${form.name} saved to lexicon`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err, "Could not save object"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="admin-setup-form" className="lab-dialog max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-widest">{specimen ? "Edit specimen" : "New specimen"}</DialogTitle>
          <DialogDescription className="text-slate-400">Link an NFC tag to an object and give it a spore word.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="grid gap-5">
          <div>
            <label className="lab-label">NFC tag ID</label>
            <div className="mt-2 flex gap-2">
              <input
                ref={tagRef}
                data-testid="specimen-tag-input"
                required
                value={form.tag_uid}
                onChange={set("tag_uid")}
                onKeyDown={onTagKey}
                placeholder="Click 'Scan tag' then place the object on the reader"
                className="lab-input flex-1 font-mono"
              />
              <button type="button" data-testid="specimen-scan-tag-button" onClick={() => { setForm((f) => ({ ...f, tag_uid: "" })); tagRef.current?.focus(); }} className="lab-btn-ghost">
                <Nfc size={16} /> Scan tag
              </button>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-[1fr_1fr_90px]">
            <div>
              <label className="lab-label">Object name</label>
              <input ref={nameRef} data-testid="specimen-name-input" required value={form.name} onChange={set("name")} className="lab-input mt-2 w-full" placeholder="e.g. Feather" />
            </div>
            <div>
              <label className="lab-label">Hive word</label>
              <input data-testid="specimen-spore-word-input" required value={form.spore_word} onChange={set("spore_word")} className="lab-input mt-2 w-full font-mono uppercase" placeholder="e.g. AER-GLI" />
            </div>
            <div>
              <label className="lab-label">Slot #</label>
              <input data-testid="specimen-slot-input" type="number" min="1" value={form.slot} onChange={set("slot")} className="lab-input mt-2 w-full" placeholder="auto" />
            </div>
          </div>
          <div>
            <label className="lab-label">Sound it makes</label>
            <input data-testid="specimen-sound-word-input" value={form.sound_word} onChange={set("sound_word")} className="lab-input mt-2 w-full font-mono" placeholder="e.g. fff-thrrm-tk" />
            <div className="mt-3"><VoiceControls voice={form.voice} onChange={(voice) => setForm((f) => ({ ...f, voice }))} soundWord={form.sound_word} word={form.spore_word} /></div>
          </div>
          <div>
            <label className="lab-label">Translation (hive meaning, shown beside the hive word)</label>
            <input data-testid="specimen-meaning-input" value={form.meaning} onChange={set("meaning")} className="lab-input mt-2 w-full font-mono" placeholder="e.g. the day on the water" />
          </div>
          <MemoryFields memory={form.memory} sfxPrompt={form.sfx_prompt || ""} onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} idPrefix="specimen-memory" />
          <RiddleFields riddle={form.riddle} name={form.name} memory={form.memory} onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} idPrefix="specimen-riddle" />
          <div>
            <div className="flex items-center justify-between">
              <label className="lab-label">Icon</label>
              <span data-testid="specimen-selected-icon" className="flex items-center gap-2 font-mono text-xs text-emerald-300">
                <SpecimenIcon name={form.icon} size={18} /> {form.icon}
              </span>
            </div>
            <div className="mt-2"><IconPicker value={form.icon} onChange={(icon) => setForm((f) => ({ ...f, icon }))} /></div>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" data-testid="specimen-cancel-button" onClick={onClose} className="lab-btn-ghost">Cancel</button>
            <button data-testid="specimen-save-button" disabled={busy} className="lab-btn-primary">{busy ? "Saving..." : "Save specimen"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
